import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Container } from "reactstrap";

import { HeadingWithBackArrow } from "../../../components/HeadingWithBackArrow";
import DataTable from "../../../components/DataTable/DataTable";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { getLockedSessionContent, getResetActivityContents } from "../../../api/api.service";
import { truncateString } from "../../../utils/commonUtils";
import typography from "../typography";
import editIcon from "../../../assets/images/app/common/pencil-icon.svg";

// The activities that ship inside the client and, unlike every other activity,
// have no AudioVideoContent row — they're permanently excluded from the
// Activity Slot picker (see data.js). This is the only place their content is
// editable. Two kinds share this list:
//
//   - Calm's Micro Reset and Focus's breathing exercise, one per state
//     (LockedSessionContent): Title/Goal/Description/Teacher Instructions/
//     thumbnail, each optional, edited on EditLockedSession.
//   - The built-in resets — Comfort's Big Hug Squeeze and Hand Compression,
//     Stuck's Thumb Peek and Color Hunt, Energy's Cross-Body Switch and
//     Standing Cross March, Focus's Cross-Crawl Tap and Seated Cross
//     Crawl, Calm's Alternating Taps — one per activity
//     (BuiltInActivityContent): always fully seeded, plus background music,
//     edited on EditResetActivity. Big Hug Squeeze used to be edited as
//     Comfort's locked session; its content moved there with any edits intact.
//
// This used to render both editable forms stacked directly on this screen.
// That was the wrong shape for two reasons: an admin arriving here saw two
// long forms with no indication of which activity already had content set,
// and it was the only editing surface in Content Library that didn't follow
// the list-then-edit flow every other item uses. It's now a listing that
// matches the audio/video table — thumbnail, name, state, edit action — with
// the form on its own route (EditLockedSession).
//
// The locked-session rows are fixed rather than fetched as a collection: there
// is no "list locked sessions" endpoint, because the set is not data — it is
// exactly these states, decided by which sessions are compiled into the
// client. Each row's saved content is fetched individually to show what's
// already been set. The reset rows come from one list request.
const LOCKED_SESSIONS = [
    { stateKey: "calm", stateLabel: typography.CALM, defaultTitle: "Micro Reset" },
    { stateKey: "focus", stateLabel: typography.FOCUS, defaultTitle: "Calm Me Now" },
];

// Which home state each built-in reset appears under. The list of resets
// itself comes from the backend; this only labels them.
const RESET_STATE_LABELS = {
    BIG_HUG_SQUEEZE: typography.COMFORT,
    HAND_COMPRESSION: typography.COMFORT,
    COLOR_HUNT: typography.CLARITY,
    THUMB_PEEK: typography.CLARITY,
    CROSS_BODY_SWITCH: typography.ENERGY,
    STANDING_CROSS_MARCH: typography.ENERGY,
    SEATED_CROSS_CRAWL: typography.FOCUS,
    CROSS_CRAWL_TAP: typography.FOCUS,
    ALTERNATING_TAPS: typography.CALM,
};

export default function LockedSessions() {
    const history = useHistory();
    const [rows, setRows] = useState([]);
    const [isFetching, setIsFetching] = useState(true);

    const loadRows = useCallback(async () => {
        setIsFetching(true);
        // Settled, not all: one request failing to load should still show the
        // rest rather than blanking the whole table.
        const [resets, ...results] = await Promise.allSettled([
            getResetActivityContents(),
            ...LOCKED_SESSIONS.map((session) => getLockedSessionContent(session.stateKey)),
        ]);

        const resetRows =
            resets.status === "fulfilled"
                ? (resets.value?.data || []).map((content) => ({
                      kind: "reset",
                      Id: content.activity,
                      activity: content.activity,
                      stateLabel: RESET_STATE_LABELS[content.activity] || "-",
                      title: content.title,
                      goal: content.goal,
                      thumbnailUrl: content.thumbnailUrl,
                      musicEnabled: content.backgroundMusicEnabled,
                      musicIsDefault: content.backgroundMusicIsDefault,
                  }))
                : [];

        setRows([
            ...LOCKED_SESSIONS.map((session, index) => {
                const result = results[index];
                const content = result.status === "fulfilled" ? result.value : null;
                return {
                    ...session,
                    kind: "locked",
                    Id: session.stateKey,
                    // Null title means no admin has renamed it, so the card
                    // still shows the client's built-in name — display that,
                    // flagged as the default, rather than an empty cell.
                    title: content?.title || null,
                    goal: content?.goal || null,
                    thumbnailUrl: content?.thumbnailUrl || null,
                    failed: result.status === "rejected",
                };
            }),
            ...resetRows,
        ]);
        setIsFetching(false);
    }, []);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const backButtonHandler = () => {
        history.push(APP_ROUTES.CONTENT_LIBRARY);
    };

    const handleEdit = (row) => {
        if (row.kind === "reset") {
            history.push(`${APP_ROUTES.EDIT_RESET_ACTIVITY}/${row.activity}`);
        } else {
            history.push(`${APP_ROUTES.EDIT_LOCKED_SESSION}/${row.stateKey}`);
        }
    };

    // page-content + .customer-management, same as Listing.js. Not cosmetic:
    // every Content Library style — including .content-thumb-image's 48x48 —
    // is nested under .customer-management in Styles.scss, so a page rendered
    // outside it gets none of them. Without this wrapper the thumbnail had no
    // width or height at all and painted at the image's natural size, which
    // shoved the whole table off to the side.
    return (
        <div className="page-content">
            <Container fluid className="bls-feedback customer-management">
                <div className="edit-notification-heading">
                    <HeadingWithBackArrow
                        backArrowClickHandler={backButtonHandler}
                        headingTitle={typography.MANAGE_LOCKED_SESSIONS}
                        showToggle={false}
                    />
                </div>

                <DataTable
                data={rows}
                loading={isFetching}
                config={[
                    {
                        title: "",
                        render: (row) => (
                            <div className="content-thumb-cell">
                                {row?.thumbnailUrl ? (
                                    // eslint-disable-next-line jsx-a11y/alt-text
                                    <img src={row.thumbnailUrl} alt="" className="content-thumb-image" />
                                ) : (
                                    <div className="content-thumb-placeholder content-type-video" />
                                )}
                            </div>
                        ),
                    },
                    {
                        title: typography.TITLE,
                        render: (row) => (
                            <div className="ms-2">
                                <div className="standard-typo-14-400">
                                    {truncateString(row?.title || row?.defaultTitle, 40)}
                                </div>
                                <div className="standard-typo-12-400-ld">
                                    {row?.kind === "reset"
                                        ? typography.BUILT_IN_RESET
                                        : row?.title
                                          ? typography.CUSTOM_TITLE
                                          : typography.DEFAULT_TITLE}
                                </div>
                            </div>
                        ),
                    },
                    {
                        title: typography.STATE,
                        render: (row) => <div className="standard-typo-14-400">{row?.stateLabel}</div>,
                    },
                    {
                        title: typography.GOAL,
                        render: (row) => (
                            <div className="standard-typo-14-400">{truncateString(row?.goal, 60) || "-"}</div>
                        ),
                    },
                    {
                        title: typography.MUSIC,
                        render: (row) => (
                            <div className="standard-typo-14-400">
                                {row?.kind !== "reset"
                                    ? "-"
                                    : !row.musicEnabled
                                      ? typography.MUSIC_OFF
                                      : `${typography.MUSIC_ON} · ${row.musicIsDefault ? typography.DEFAULT_ASSET : typography.UPLOADED_ASSET}`}
                            </div>
                        ),
                    },
                    {
                        title: typography.ACTION,
                        render: (row) => (
                            <div className="action-icons-con">
                                <img
                                    src={editIcon}
                                    alt="Edit"
                                    className="cursor-pointer"
                                    onClick={() => handleEdit(row)}
                                />
                            </div>
                        ),
                    },
                    ]}
                />
            </Container>
        </div>
    );
}
